import axios from 'axios';
import { store as reduxStore } from 'index';
import { getAPIURL } from '../config';
import Jwt from './Jwt';

let isRequestingRefreshToken = false;
let requestsToRefresh = [];

const UNAUTHORIZED = 401;
const api = axios.create({
  baseURL: getAPIURL(),
  withCredentials: true,
  headers: { 'X-Requested-With': 'XMLHttpRequest' },
});

const unauthorizedExceptionRouts = [
  /^\/user\/reset-password$/,
  /^\/user\/forgot$/,
  /^\/user\/login$/,
  /^\/airbill\/[^/]+\/signature\/[^/]+$/,
];

// Where AeroStudies sends a pilot after an assessment. The iPad app's pilots
// arrive signed out and must not be sent to the sign-in page.
const optionalSignInRoutes = [/^\/aerostudies\/return$/];

api.interceptors.response.use(
  response => response,
  error => {
    const { response, config } = error;
    // Auth endpoints (refresh, logout) opt out of the 401 handling below. A
    // failed refresh must reject so the caller can sign the user out; routed
    // through this handler it would queue itself behind the refresh it *is*
    // and never settle, leaving the app on a spinner forever.
    if (config?.skipAuthRefresh) return Promise.reject(error);
    if (response?.status === UNAUTHORIZED) {
      if (!isRequestingRefreshToken) {
        const state = reduxStore.getState();
        const pathname = state?.router?.location?.pathname;

        if (unauthorizedExceptionRouts.some(route => route.test(pathname))) return false;
        // Pages that also work signed out. Only skip the sign-in redirect when
        // there is no session to refresh, so a signed-in user's expired token is
        // still refreshed as usual.
        if (
          optionalSignInRoutes.some(route => route.test(pathname)) &&
          !localStorage.getItem('refreshToken')
        ) {
          return false;
        }

        // refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          isRequestingRefreshToken = true;
          api
            .post(
              '/auth/refresh',
              {
                grant_type: 'refresh_token',
              },
              {
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                },
                skipAuthRefresh: true,
              },
            )
            .then(({ data }) => {
              console.log('token refreshed');
              Jwt.set(data.AccessToken);
              localStorage.setItem('refreshToken', data.RefreshToken);
              requestsToRefresh.forEach(cb => cb(data.AccessToken));
            })
            .catch(err => {
              console.log('unable to refresh token');
              console.log(err);
              requestsToRefresh.forEach(cb => cb(null));
              reduxStore.dispatch({ type: 'user/LOGOUT' });
            })
            .finally(() => {
              requestsToRefresh = [];
              isRequestingRefreshToken = false;
            });
        } else {
          reduxStore.dispatch({ type: 'user/LOGOUT' });
        }
      }

      // console.log(`The request is waiting for a new token.. [${response.config.url}]`)
      return new Promise((resolve, reject) => {
        // In our variable (requests that expect a new token
        // from the first request), we add a callback to resend the request
        requestsToRefresh.push(token => {
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            resolve(axios(config));
          }
          reject(error);
        });
      });
    }
    return Promise.reject(error);
  },
);

export default api;
